// src/app/auth/auth.service.ts

import { Injectable } from '@angular/core';
import { AuthenticationDetails, CognitoUser, CognitoUserSession, CognitoIdToken, CognitoAccessToken, CognitoRefreshToken, CognitoUserPool } from 'amazon-cognito-identity-js';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private userPool: CognitoUserPool;

  // Resolves once any SSO redirect-callback on this page load has been processed
  // (implicit: parse the URL fragment; code flow: exchange ?code at /oauth2/token).
  // isAuthenticated() awaits this so the guard/login don't race the token exchange.
  public ssoReady: Promise<boolean> = Promise.resolve(false);

  // Dedupe concurrent silent refreshes onto a single in-flight request.
  private refreshPromise: Promise<boolean> | null = null;
  // One hard-logout redirect per page load when refresh isn't possible.
  private sessionExpiredHandled = false;

  // Store cognitoUser for NEW_PASSWORD_REQUIRED challenge
  private pendingUser: CognitoUser | null = null;
  private pendingUserAttributes: any = null;

  constructor(private router: Router) {
    // 1. Configure the Cognito SDK to use localStorage.
    const poolData = {
      UserPoolId: environment.userPoolId,
      ClientId: environment.clientId,
      Storage: localStorage // This is the key change!
    };
    this.userPool = new CognitoUserPool(poolData);

    // 2. If SSO is enabled, handle the callback on app init
    // Don't navigate here - let the auth guard handle routing
    if (environment.sso) {
      this.ssoReady = this.handleSSOCallback();
    }
  }

  /**
   * SSO: Initiates the login flow by redirecting to Cognito Hosted UI
   */
  public async loginWithSSO(): Promise<void> {
    if (!environment.sso) {
      console.error('SSO is not enabled in environment');
      return;
    }

    // Circuit breaker: if we've tried to redirect to the hosted UI 3+ times in
    // 20s, we're in a loop (Cognito keeps bouncing us back with something the app
    // can't consume). Halt with an alert() — it freezes the page so the loop stops
    // and the diagnostic is readable even though the console wipes on each reload.
    const LOOP_KEY = 'sso_redirect_attempts';
    const now = Date.now();
    let attempts: number[] = [];
    try { attempts = JSON.parse(sessionStorage.getItem(LOOP_KEY) || '[]'); } catch { attempts = []; }
    attempts = attempts.filter(t => now - t < 20000);
    attempts.push(now);
    sessionStorage.setItem(LOOP_KEY, JSON.stringify(attempts));

    if (attempts.length >= 3) {
      sessionStorage.removeItem(LOOP_KEY);
      const search = window.location.search || '(none)';
      const hash = window.location.hash || '(none)';
      const diag =
        'SSO redirect loop detected (3 redirects in 20s) — halted so you can inspect.\n\n' +
        'What Cognito last sent back to ' + environment.ssoRedirectUri + ':\n' +
        '  query (search): ' + search + '\n' +
        '  fragment (hash): ' + (hash.length > 80 ? hash.slice(0, 80) + '…' : hash) + '\n\n' +
        'How to read it:\n' +
        '• "?code=..." in the query → Cognito is using AUTHORIZATION CODE flow, but the app expects IMPLICIT (response_type=token). Enable "Implicit grant" on the app client (or we switch the app to code flow).\n' +
        '• "#error=..." → read error_description (most often: redirect_uri / callback URL not registered, or implicit grant disabled).\n' +
        '• "#id_token=..." present but still looping → the token isn\'t being consumed; check the console for errors (turn on "Preserve log").\n' +
        '• both "(none)" → Cognito returned nothing usable; check Allowed callback URLs = ' + environment.ssoRedirectUri;
      console.error('[SSO] LOOP BREAKER —', diag);
      alert(diag);
      return;
    }

    const ssoUrl = await this.buildSSOUrl();
    window.location.href = ssoUrl;
  }

  /** True when this org is configured for OAuth Authorization Code flow (PKCE). */
  private get usesCodeFlow(): boolean {
    return (environment.responseType || '').toLowerCase() === 'code';
  }

  /** True when this org uses SSO Authorization Code flow (e.g. cbhi). Public so the
   *  interceptor can decide whether a 401 means "session dead, re-login" vs. a
   *  legitimate permission error to leave alone. */
  public get usesSsoCodeFlow(): boolean {
    return !!environment.sso && this.usesCodeFlow;
  }

  /**
   * SSO: Builds the Cognito hosted-UI authorize URL.
   * Implicit orgs (responseType 'token') use the classic fragment flow.
   * Code-flow orgs (responseType 'code') additionally generate a PKCE
   * code_verifier/code_challenge (verifier stashed in sessionStorage for the later
   * token exchange) plus a CSRF state value.
   */
  private async buildSSOUrl(): Promise<string> {
    const params = new URLSearchParams({
      client_id: environment.ssoClientId,
      response_type: this.usesCodeFlow ? 'code' : (environment.responseType || 'token'),
      scope: environment.scope,
      redirect_uri: environment.ssoRedirectUri,
      identity_provider: 'Crestwood-SSO' // The SAML provider we created
    });

    if (this.usesCodeFlow) {
      const verifier = this.randomUrlSafe(64);
      const challenge = await this.sha256Base64Url(verifier);
      const state = this.randomUrlSafe(24);
      sessionStorage.setItem('pkce_verifier', verifier);
      sessionStorage.setItem('oauth_state', state);
      params.set('code_challenge', challenge);
      params.set('code_challenge_method', 'S256');
      params.set('state', state);
    }

    return `${environment.ssoDomain}/oauth2/authorize?${params.toString()}`;
  }

  /**
   * SSO: Handles the redirect back from Cognito. Dispatches to the implicit
   * (fragment) or code (query + token exchange) handler. Resolves true if a
   * session was established on this page load.
   */
  private async handleSSOCallback(): Promise<boolean> {
    return this.usesCodeFlow ? this.handleCodeCallback() : this.handleImplicitCallback();
  }

  /** Authorization Code + PKCE: exchange ?code=… at /oauth2/token for tokens. */
  private async handleCodeCallback(): Promise<boolean> {
    const params = new URLSearchParams(window.location.search);

    const error = params.get('error');
    if (error) {
      console.error('[SSO] Cognito returned an error in the redirect:',
        '\n  error:', error,
        '\n  error_description:', params.get('error_description'));
      this.cleanUrl();
      return false;
    }

    const code = params.get('code');
    if (!code) {
      // Not a callback — user just navigated here without logging in yet.
      return false;
    }

    // CSRF protection: the returned state must match what we sent.
    const expectedState = sessionStorage.getItem('oauth_state');
    if (expectedState && params.get('state') !== expectedState) {
      console.error('[SSO] state mismatch on callback — ignoring (possible CSRF).');
      this.cleanUrl();
      return false;
    }

    const verifier = sessionStorage.getItem('pkce_verifier');
    if (!verifier) {
      console.error('[SSO] missing PKCE verifier — cannot exchange the code.');
      this.cleanUrl();
      return false;
    }

    try {
      const body = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: environment.ssoClientId,
        code,
        redirect_uri: environment.ssoRedirectUri,
        code_verifier: verifier
      });
      const resp = await fetch(`${environment.ssoDomain}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      });
      if (!resp.ok) {
        console.error('[SSO] token exchange failed:', resp.status, await resp.text());
        this.cleanUrl();
        return false;
      }
      const data = await resp.json();
      this.storeSSOTokens(data.id_token, data.access_token, String(data.expires_in), data.refresh_token);

      sessionStorage.removeItem('pkce_verifier');
      sessionStorage.removeItem('oauth_state');
      sessionStorage.removeItem('sso_redirect_attempts'); // good login resets the loop breaker
      this.cleanUrl();
      return true;
    } catch (e) {
      console.error('[SSO] token exchange error:', e);
      this.cleanUrl();
      return false;
    }
  }

  /** Implicit flow: tokens arrive in the URL #fragment. */
  private async handleImplicitCallback(): Promise<boolean> {
    const hash = window.location.hash;

    // Cognito returns errors in the fragment too (e.g. #error=...&error_description=...).
    if (hash && hash.includes('error')) {
      const errParams = new URLSearchParams(hash.substring(1));
      console.error('[SSO] Cognito returned an error in the redirect:',
        '\n  error:', errParams.get('error'),
        '\n  error_description:', errParams.get('error_description'));
    }

    if (!hash || !hash.includes('id_token')) {
      return false;
    }

    const params = new URLSearchParams(hash.substring(1));
    const idToken = params.get('id_token');
    const accessToken = params.get('access_token');
    const expiresIn = params.get('expires_in');

    if (idToken && accessToken) {
      this.storeSSOTokens(idToken, accessToken, expiresIn);
      this.cleanUrl();
      sessionStorage.removeItem('sso_redirect_attempts');
      return true;
    }
    return false;
  }

  /** Strip the OAuth params (?code / #id_token) from the URL bar after handling. */
  private cleanUrl(): void {
    window.history.replaceState(null, '', window.location.pathname);
  }

  /**
   * SSO: Stores tokens in localStorage in Cognito-SDK key format so the rest of
   * the app (getToken/getAccessToken/isAuthenticated) reads them unchanged. Code
   * flow additionally persists the refresh token + an absolute expiry timestamp,
   * used for silent refresh.
   */
  private storeSSOTokens(idToken: string, accessToken: string, expiresIn: string | null, refreshToken?: string | null): void {
    const keyPrefix = `CognitoIdentityServiceProvider.${environment.ssoClientId}`;

    // Decode the ID token to get the username
    const payload = this.decodeJWT(idToken);
    const username = payload['cognito:username'] || payload.email;

    localStorage.setItem(`${keyPrefix}.LastAuthUser`, username);
    localStorage.setItem(`${keyPrefix}.${username}.idToken`, idToken);
    localStorage.setItem(`${keyPrefix}.${username}.accessToken`, accessToken);
    if (refreshToken) {
      localStorage.setItem(`${keyPrefix}.${username}.refreshToken`, refreshToken);
    }
    if (expiresIn) {
      const expiryMs = Date.now() + (parseInt(expiresIn, 10) * 1000);
      localStorage.setItem(`${keyPrefix}.${username}.tokenExpiry`, String(expiryMs));
    }
  }

  // ── PKCE helpers ───────────────────────────────────────────────────────────
  private randomUrlSafe(numBytes: number): string {
    const arr = new Uint8Array(numBytes);
    crypto.getRandomValues(arr);
    return this.base64Url(arr.buffer);
  }

  private async sha256Base64Url(input: string): Promise<string> {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
    return this.base64Url(digest);
  }

  private base64Url(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let str = '';
    for (let i = 0; i < bytes.length; i++) {
      str += String.fromCharCode(bytes[i]);
    }
    return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  // ── Silent refresh (code flow only) ─────────────────────────────────────────

  /** True when a silent refresh is possible: SSO code flow + a stored refresh token. */
  public canRefresh(): boolean {
    if (!environment.sso || !this.usesCodeFlow) return false;
    const keyPrefix = `CognitoIdentityServiceProvider.${environment.ssoClientId}`;
    const lastAuthUser = localStorage.getItem(`${keyPrefix}.LastAuthUser`);
    return !!(lastAuthUser && localStorage.getItem(`${keyPrefix}.${lastAuthUser}.refreshToken`));
  }

  /** True when the stored token is within `skewSeconds` of expiry (or already expired). */
  public isTokenExpiringSoon(skewSeconds = 60): boolean {
    if (!environment.sso || !this.usesCodeFlow) return false;
    const keyPrefix = `CognitoIdentityServiceProvider.${environment.ssoClientId}`;
    const lastAuthUser = localStorage.getItem(`${keyPrefix}.LastAuthUser`);
    if (!lastAuthUser) return false;
    const expiry = localStorage.getItem(`${keyPrefix}.${lastAuthUser}.tokenExpiry`);
    if (!expiry) return false;
    return Date.now() >= (parseInt(expiry, 10) - skewSeconds * 1000);
  }

  /**
   * Silently exchanges the stored refresh token for fresh id/access tokens.
   * Deduped: concurrent callers share one in-flight request. Resolves false if
   * there's no refresh token or the exchange fails (caller should then re-login).
   */
  public refreshSession(): Promise<boolean> {
    if (this.refreshPromise) return this.refreshPromise;
    this.refreshPromise = this.doRefresh().finally(() => { this.refreshPromise = null; });
    return this.refreshPromise;
  }

  private async doRefresh(): Promise<boolean> {
    if (!this.canRefresh()) return false;
    const keyPrefix = `CognitoIdentityServiceProvider.${environment.ssoClientId}`;
    const lastAuthUser = localStorage.getItem(`${keyPrefix}.LastAuthUser`)!;
    const refreshToken = localStorage.getItem(`${keyPrefix}.${lastAuthUser}.refreshToken`)!;
    try {
      const body = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: environment.ssoClientId,
        refresh_token: refreshToken
      });
      const resp = await fetch(`${environment.ssoDomain}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      });
      if (!resp.ok) {
        console.error('[SSO] silent refresh failed:', resp.status);
        return false;
      }
      const data = await resp.json();
      // Cognito's refresh grant returns new id/access but NOT a new refresh token,
      // so we re-persist the existing one.
      this.storeSSOTokens(data.id_token, data.access_token, String(data.expires_in), refreshToken);
      return true;
    } catch (e) {
      console.error('[SSO] silent refresh error:', e);
      return false;
    }
  }

  /**
   * Hard session expiry (no refresh token, or refresh failed). Stashes the current
   * route so we can return the user there after re-login, then kicks off SSO login
   * (seamless if the Cognito session cookie is still alive). Deduped so a burst of
   * 401s triggers a single redirect.
   */
  public handleSessionExpired(): void {
    if (this.sessionExpiredHandled) return;
    this.sessionExpiredHandled = true;
    try { sessionStorage.setItem('post_login_redirect', this.router.url); } catch { /* ignore */ }
    if (environment.sso) {
      this.loginWithSSO();
    } else {
      this.router.navigate(['/login']);
    }
  }

  /**
   * Decodes a JWT token (simple base64 decode, no verification)
   */
  private decodeJWT(token: string): any {
    try {
      const payload = token.split('.')[1];
      const decoded = atob(payload);
      return JSON.parse(decoded);
    } catch (e) {
      console.error('Error decoding JWT:', e);
      return {};
    }
  }

  /**
   * SSO: Logs out and redirects to Cognito logout endpoint
   */
  public logoutSSO(): void {
    if (!environment.sso) {
      console.error('SSO is not enabled in environment');
      return;
    }

    // Clear local storage
    localStorage.clear();

    // Build logout URL
    const params = new URLSearchParams({
      client_id: environment.ssoClientId,
      logout_uri: environment.ssoRedirectUri
    });

    const logoutUrl = `${environment.ssoDomain}/logout?${params.toString()}`;
    window.location.href = logoutUrl;
  }

  public getCurrentUser(): Promise<CognitoUser | null> {
    return new Promise((resolve, reject) => {
      const cognitoUser = this.userPool.getCurrentUser();
      if (cognitoUser) {
        resolve(cognitoUser);
      } else {
        resolve(null);
      }
    });
  }

  /**
   * Gets the email address of the currently authenticated user.
   * Returns the email from the user's attributes (ID token).
   * Works for both SSO and non-SSO flows.
   */
  public getUserEmail(): Promise<string | null> {
    return new Promise((resolve, reject) => {
      // For SSO, check localStorage directly
      if (environment.sso) {
        const keyPrefix = `CognitoIdentityServiceProvider.${environment.ssoClientId}`;
        const lastAuthUser = localStorage.getItem(`${keyPrefix}.LastAuthUser`);

        if (lastAuthUser) {
          const idToken = localStorage.getItem(`${keyPrefix}.${lastAuthUser}.idToken`);
          if (idToken) {
            const payload = this.decodeJWT(idToken);
            const email = payload.email || payload['cognito:username'];
            resolve(email);
            return;
          }
        }
        resolve(null);
        return;
      }

      // For non-SSO, use Cognito SDK
      const cognitoUser = this.userPool.getCurrentUser();
      if (!cognitoUser) {
        resolve(null);
        return;
      }

      cognitoUser.getSession((err: any, session: CognitoUserSession) => {
        if (err) {
          reject(err);
          return;
        }

        if (session && session.isValid()) {
          const idToken = session.getIdToken();
          const email = idToken.payload.email || idToken.payload['cognito:username'];
          resolve(email);
        } else {
          resolve(null);
        }
      });
    });
  }

  signIn(username: string, password: string): Promise<any> {
    const authenticationDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    });
    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: this.userPool,
    });

    return new Promise((resolve, reject) => {
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (result) => resolve(result),
        onFailure: (err) => reject(err),
        newPasswordRequired: (userAttributes) => {
          // Store the cognitoUser and attributes for completing the challenge later
          this.pendingUser = cognitoUser;
          // Remove non-writable/read-only attributes that Cognito doesn't allow in the response
          delete userAttributes.email_verified;
          delete userAttributes.phone_number_verified;
          delete userAttributes.email;  // Cannot modify already provided email
          delete userAttributes.sub;    // User ID, read-only
          this.pendingUserAttributes = userAttributes;
          resolve('NEW_PASSWORD_REQUIRED');
        },
        mfaRequired: () => resolve('MFA_REQUIRED'),
      });
    });
  }

  /**
   * Completes the NEW_PASSWORD_REQUIRED challenge with the user's new password
   */
  completeNewPasswordChallenge(newPassword: string): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.pendingUser) {
        reject(new Error('No pending password challenge. Please sign in again.'));
        return;
      }

      this.pendingUser.completeNewPasswordChallenge(newPassword, this.pendingUserAttributes, {
        onSuccess: (result) => {
          // Clear the pending user
          this.pendingUser = null;
          this.pendingUserAttributes = null;
          resolve(result);
        },
        onFailure: (err) => {
          reject(err);
        }
      });
    });
  }

  confirmSignUp(username: string, confirmationCode: string): Promise<any> {
    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: this.userPool,
    });

    return new Promise((resolve, reject) => {
      cognitoUser.confirmRegistration(confirmationCode, true, (err, result) => {
        if (err) {
          reject(err);
        } else {
          resolve(result);
        }
      });
    });
  }

  async isAuthenticated(): Promise<boolean> {
    // For SSO, wait for any in-flight redirect-callback handling (code exchange),
    // then check localStorage for a stored ID token.
    if (environment.sso) {
      await this.ssoReady;
      const keyPrefix = `CognitoIdentityServiceProvider.${environment.ssoClientId}`;
      const lastAuthUser = localStorage.getItem(`${keyPrefix}.LastAuthUser`);
      const hasIdToken = lastAuthUser && localStorage.getItem(`${keyPrefix}.${lastAuthUser}.idToken`);
      return !!hasIdToken;
    }

    // For non-SSO, use Cognito SDK
    return new Promise((resolve) => {
      const cognitoUser = this.userPool.getCurrentUser();

      if (!cognitoUser) {
        resolve(false);
        return;
      }

      cognitoUser.getSession((err: any, session: any) => {
        if (err) {
          console.error("AuthService: Error getting session:", err);
          resolve(false);
        } else if (session && session.isValid()) {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  // Signs the user out. The SDK will automatically clear the tokens from localStorage.
  signOut(): void {
    if (environment.sso) {
      this.logoutSSO();
    } else {
      const cognitoUser = this.userPool.getCurrentUser();
      if (cognitoUser) {
        cognitoUser.signOut();
      }
    }
  }

  /**
   * Retrieves the ID Token from the current authenticated user's session.
   * Works for both SSO and non-SSO flows.
   */
  getToken(): string | null {
    // For SSO, get token from localStorage
    if (environment.sso) {
      const keyPrefix = `CognitoIdentityServiceProvider.${environment.ssoClientId}`;
      const lastAuthUser = localStorage.getItem(`${keyPrefix}.LastAuthUser`);

      if (lastAuthUser) {
        return localStorage.getItem(`${keyPrefix}.${lastAuthUser}.idToken`);
      }
      return null;
    }

    // For non-SSO, use Cognito SDK
    const cognitoUser = this.userPool.getCurrentUser();
    if (cognitoUser != null) {
      const session = cognitoUser.getSignInUserSession();
      if (session && session.isValid()) {
        const idToken = session.getIdToken().getJwtToken();
        return idToken;
      }
      // Fallback: SDK session may not be hydrated yet on first page load.
      // The Cognito SDK caches tokens in localStorage — read the idToken directly.
      // (Mirrors getAccessToken's fallback; required since cbhi sends the ID token
      // to the timesheets/timecards APIs — without this the first call on load
      // goes out with no Bearer token and the backend authorizer 401s.)
      const username = (cognitoUser as any).username || (cognitoUser as any).getUsername?.();
      const keyPrefix = `CognitoIdentityServiceProvider.${environment.clientId}`;
      return localStorage.getItem(`${keyPrefix}.${username}.idToken`);
    }
    return null;
  }

  /**
   * Retrieves the Access Token from the current authenticated user's session.
   * The access token is required by APIs that use a Cognito Lambda authorizer
   * (which validates token_use: "access").
   * Works for both SSO and non-SSO flows.
   */
  getAccessToken(): string | null {
    // For SSO, get access token from localStorage
    if (environment.sso) {
      const keyPrefix = `CognitoIdentityServiceProvider.${environment.ssoClientId}`;
      const lastAuthUser = localStorage.getItem(`${keyPrefix}.LastAuthUser`);
      if (lastAuthUser) {
        return localStorage.getItem(`${keyPrefix}.${lastAuthUser}.accessToken`);
      }
      return null;
    }

    // For non-SSO, use Cognito SDK
    const cognitoUser = this.userPool.getCurrentUser();
    if (cognitoUser != null) {
      const session = cognitoUser.getSignInUserSession();
      if (session && session.isValid()) {
        return session.getAccessToken().getJwtToken();
      }
      // Fallback: SDK session may not be hydrated yet on first page load.
      // The Cognito SDK caches tokens in localStorage — read directly from there.
      const username = (cognitoUser as any).username || (cognitoUser as any).getUsername?.();
      const keyPrefix = `CognitoIdentityServiceProvider.${environment.clientId}`;
      return localStorage.getItem(`${keyPrefix}.${username}.accessToken`);
    }
    return null;
  }

  forgotPassword(username: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: this.userPool
      });

      cognitoUser.forgotPassword({
        onSuccess: (result) => {
          resolve(result);
        },
        onFailure: (err) => {
          reject(err);
        }
      });
    });
  }

  confirmNewPassword(username: string, verificationCode: string, newPassword): Promise<any> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: this.userPool
      });

      cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess: (result) => {
          resolve(result);
        },
        onFailure: (err) => {
          reject(err);
        }
      });
    });
  }
}
